import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Grid,
  Typography,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  Divider,
  Skeleton,
  Button,
  Box,
  SelectChangeEvent,
  IconButton,
  Popover,
  Tooltip,
  CircularProgress,
  Autocomplete,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Download as DownloadIcon } from '@mui/icons-material';
import Filters from './Filters';
import { useApiSend, useApiGet } from '../../utils/httpClientTs';
import {
  getReport,
  getReportList,
  getReportExcel,
} from '../../endpoints/reportsTs';
import {
  Report as ReportType,
  ReportResponse,
  ReportColumns,
  QuickFilter,
  Filter,
} from '../../types/reports';

interface FilterState {
  [key: string]: any; // this will be the value of that filter after selection ie {'spd_id': '1234'}
}

const Report: React.FC = () => {
  let [searchParams, setSearchParams] = useSearchParams();

  const updateSearchParams = useCallback(
    (key: string, value: string) => {
      if (!value || value === '') {
        searchParams.delete(key);
      } else {
        searchParams.set(key, value);
      }
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const [selectedReport, setSelectedReport] = useState<string>(
    searchParams.get('report') || ''
  );
  const [quickFilter, setQuickFilter] = useState<string>(
    searchParams.get('quickFilter') || ''
  );
  const [reportQuickFilters, setReportQuickFilters] = useState<
    string[] | QuickFilter[] | null
  >(null);
  const [filters, setFilters] = useState<FilterState>({});
  const [disabledFilters, setDisabledFilters] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState<boolean>(true);
  const [rowData, setRowData] = useState<any[]>([]);
  const [reportError, setReportError] = useState<string | null>(null);
  const [columns, setColumns] = useState<ReportColumns[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);
  const [downloadFormat, setDownloadFormat] = useState<'excel'>('excel');

  const [downloadSelectorAnchor, setDownloadSelectorAnchor] =
    useState<HTMLElement | null>(null);

  const { mutate } = useApiSend(
    getReport,
    (ret: ReportResponse) => {
      setRowData(ret.data);
      setColumns(ret.columns);
      setReportError(null);
      setIsLoading(false);
    },
    (error: Error) => {
      setReportError(error.message);
      setIsLoading(false);
    }
  );

  const resetRowData = () => {
    setRowData([]);
    setColumns([]);
    setReportError(null);
  };

  const { data: reportList } = useApiGet<ReportType[]>(
    ['reportList'],
    getReportList,
    {
      enabled: true,
    }
  );

  const downloadReport = async (data: any) => {
    try {
      setIsDownloadLoading(true);
      const response = await getReportExcel(data);

      // Create a blob URL and trigger download
      const blob = new Blob([response], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Set filename based on format
      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, '-');
      const filename = `report_${timestamp}.xlsx`;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      window.URL.revokeObjectURL(url);

      setIsDownloadLoading(false);
    } catch (error) {
      console.error('Download failed:', error);
      setIsDownloadLoading(false);
    }
  };

  useEffect(() => {
    if (reportList && selectedReport) {
      const report = reportList.find(
        (r: ReportType) => r.id === selectedReport
      );
      if (report) {
        // Handle quick_filters - could be a JSON string or already an array
        let parsedQuickFilters: string[] | QuickFilter[] = [];
        if (report.quick_filters) {
          if (Array.isArray(report.quick_filters)) {
            // If it's already an array, use it directly
            parsedQuickFilters = report.quick_filters;
          } else if (typeof report.quick_filters === 'string') {
            // If it's a string, try to parse it as JSON
            try {
              parsedQuickFilters = JSON.parse(report.quick_filters);
            } catch (e) {
              console.warn('Failed to parse quick_filters:', e);
              parsedQuickFilters = [];
            }
          }
        }

        setReportQuickFilters(
          Array.isArray(parsedQuickFilters) ? parsedQuickFilters : []
        );
      }
    }
  }, [reportList, selectedReport]);

  useEffect(() => {
    if (selectedReport) {
      updateSearchParams('report', selectedReport);
    }
  }, [selectedReport, updateSearchParams]);

  const applyQuickFilterFilters = useCallback(
    (quickFilterValue: string, reportId?: string) => {
      const targetReportId = reportId || selectedReport;
      if (reportList && targetReportId) {
        const report = reportList.find(
          (r: ReportType) => r.id === targetReportId
        );
        if (report && report.quick_filters) {
          let parsedQuickFilters: QuickFilter[] = report.quick_filters;

          // Find the selected quick filter and apply its filter configuration
          const selectedQuickFilter = parsedQuickFilters.find(
            (qf: string | QuickFilter) => {
              if (typeof qf === 'string') {
                return qf === quickFilterValue;
              } else {
                return (
                  qf.id === quickFilterValue || qf.name === quickFilterValue
                );
              }
            }
          );

          if (
            selectedQuickFilter &&
            typeof selectedQuickFilter === 'object' &&
            selectedQuickFilter.filters
          ) {
            // Apply the filter configuration from the quick filter
            // Convert Filter[] to FilterState object
            const filterState: FilterState = {};
            const disabledFilterKeys: string[] = [];

            // Loop through the filter array to get all FilterTypes and check disabled status
            selectedQuickFilter.filters.forEach((filter: Filter) => {
              // Use the filterType as the key
              const key = filter.filterType;
              if (key) {
                // Set the actual default value, not the filter configuration object
                filterState[key] = filter.default || null;

                // Check if this filter is disabled and add to disabled list
                if (filter.disabled) {
                  disabledFilterKeys.push(key);
                }
              }
            });

            setFilters(filterState);
            setDisabledFilters(disabledFilterKeys);
          } else if (typeof selectedQuickFilter === 'string') {
            // If it's just a string, clear filters (legacy behavior)
            setFilters({});
            setDisabledFilters([]);
          } else {
            // Default: clear filters
            setFilters({});
            setDisabledFilters([]);
          }
        }
      }
    },
    [reportList, selectedReport]
  );

  useEffect(() => {
    if (quickFilter) {
      updateSearchParams('quickFilter', quickFilter);
      // Need to set the filters based on the quickFilter
      applyQuickFilterFilters(quickFilter);
    }
  }, [quickFilter, updateSearchParams, applyQuickFilterFilters]);

  // Apply quick filter on initial load if present in URL
  useEffect(() => {
    const urlQuickFilter = searchParams.get('quickFilter');
    if (urlQuickFilter && reportList && selectedReport) {
      // Set the quick filter ID from URL
      setQuickFilter(urlQuickFilter);
      // Apply the filters using the ID
      applyQuickFilterFilters(urlQuickFilter, selectedReport);
    }
  }, [reportList, selectedReport, applyQuickFilterFilters, searchParams]);

  const updateReport = (reportId: string) => {
    setSelectedReport(reportId);
    resetRowData();
    setQuickFilter('');
    setFilters({});
    setDisabledFilters([]);

    // Find the selected report and set its quickFilters
    if (reportList) {
      const report = reportList.find((r: ReportType) => r.id === reportId);
      if (report && report.quick_filters) {
        // Quick filters are handled by applyQuickFilterFilters when needed
        // No need to parse them here as they're not used in this function
      }
    }
  };

  const updateQuickFilter = (filter: string | QuickFilter) => {
    const filterId = typeof filter === 'string' ? filter : filter.id;
    const filterName = typeof filter === 'string' ? filter : filter.name;

    setQuickFilter(filterId);
    resetRowData();
    applyQuickFilterFilters(filterName);
  };

  const generateReport = () => {
    if (!selectedReport || !reportList) return;

    const report = reportList.find((r: ReportType) => r.id === selectedReport);
    if (!report || !report.route) {
      console.error('Report or route not found:', selectedReport);
      return;
    }

    setIsLoading(true);
    (mutate as any)({
      reportId: selectedReport,
      url: report.route,
      data: {
        quickFilter: quickFilter,
        ...filters,
      },
    });
  };

  const handleGetDownload = () => {
    if (!selectedReport || !reportList) return;

    const report = reportList.find((r: ReportType) => r.id === selectedReport);
    if (!report || !report.route) {
      console.error('Report or route not found for download:', selectedReport);
      return;
    }

    downloadReport({
      reportId: selectedReport,
      url: report.route,
      data: {
        quickFilter: quickFilter,
        format: downloadFormat,

        ...filters,
      },
    });
  };

  const handleDownloadClick = (event: React.MouseEvent<HTMLElement>) => {
    setDownloadSelectorAnchor(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadSelectorAnchor(null);
  };

  const handleDownloadConfirm = (format: 'excel') => {
    setDownloadFormat(format);
    setDownloadSelectorAnchor(null);
    handleGetDownload();
  };

  // Convert columns to DataGrid format
  const getDataGridColumns = (): GridColDef[] => {
    // Calculate total width from column definitions
    const totalWidth = columns.reduce((sum, column) => {
      // Use column.width if defined, otherwise default to 150
      return sum + (column.width || 150);
    }, 0);

    return columns.map((column) => {
      const columnWidth = column.width || 150;
      const widthPercentage = (columnWidth / totalWidth) * 100;

      return {
        field: column.dataKey,
        headerName: column.label,
        width: columnWidth,
        flex: widthPercentage / 100, // Convert percentage to flex value
        minWidth: Math.max(100, columnWidth * 0.5), // Minimum 50% of defined width
        maxWidth: Math.max(300, columnWidth * 1.5), // Maximum 150% of defined width
        sortable: true,
        filterable: true,
        // resizable: true, // Removed - not available in MIT version
        valueGetter: (params: any) => {
          const value = params.row[column.dataKey];
          return value || '';
        },
      };
    });
  };

  // Add unique IDs to row data for DataGrid
  const getRowDataWithIds = () => {
    return rowData.map((row, index) => ({
      id: row.id || `row-${index}`,
      ...row,
    }));
  };

  const noDataText = () => {
    if (reportError) return reportError;
    if (!selectedReport) return 'Please select a report';
    if (isLoading) return 'Loading...';
    return 'No data available';
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h4" gutterBottom>
            Reports
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Report</InputLabel>
            <Select
              value={selectedReport}
              label="Report"
              size="small"
              onChange={(e: SelectChangeEvent<string>) =>
                updateReport(e.target.value)
              }
            >
              {reportList?.map((report: ReportType) => (
                <MenuItem key={report.id} value={report.id}>
                  {report.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          {reportQuickFilters && reportQuickFilters.length > 0 ? (
            <Autocomplete
              options={reportQuickFilters || []}
              size="small"
              getOptionLabel={(option: string | QuickFilter) =>
                typeof option === 'string' ? option : option.name
              }
              value={(() => {
                if (!quickFilter || !reportQuickFilters) return null;
                return (
                  reportQuickFilters.find((qf: string | QuickFilter) => {
                    if (typeof qf === 'string') {
                      return qf === quickFilter;
                    } else {
                      return qf.id === quickFilter;
                    }
                  }) || null
                );
              })()}
              onChange={(_, newValue: string | QuickFilter | null) => {
                if (newValue) {
                  updateQuickFilter(newValue);
                } else {
                  setQuickFilter('');
                  resetRowData();
                  setFilters({});
                  setDisabledFilters([]);
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Quick Filter" />
              )}
            />
          ) : (
            <Typography variant="body2" color="textSecondary">
              No quick filters available for this report
            </Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="outlined"
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? 'Hide' : 'Show'} Filters
            </Button>
          </Box>
        </Grid>

        {showFilters && (
          <Grid item xs={12}>
            <Filters
              reportId={selectedReport}
              filters={filters}
              updateFilters={(newFilters: Record<string, any>) => {
                console.log('Filters component updating filters:', newFilters);
                setFilters(newFilters);
              }}
              disabledFilters={disabledFilters}
            />
          </Grid>
        )}

        {/* Action Buttons and Column Selector */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 3,
            }}
          >
            {/* Left Side - Download Report and Column Selector */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {rowData.length > 0 && (
                <Tooltip title="Download Report">
                  <IconButton
                    onClick={handleDownloadClick}
                    disabled={
                      !selectedReport ||
                      rowData.length === 0 ||
                      isDownloadLoading
                    }
                    sx={{
                      backgroundColor: 'white',
                      color: 'primary.main',
                      border: '1px solid',
                      borderColor: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'grey.50',
                        borderColor: 'primary.dark',
                      },
                    }}
                  >
                    {isDownloadLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <DownloadIcon />
                    )}
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {/* Right Side - Generate Report Button */}
            <LoadingButton
              variant="contained"
              onClick={generateReport}
              loading={isLoading}
              disabled={!selectedReport}
            >
              Generate Report
            </LoadingButton>
          </Box>
        </Grid>

        {/* Download Format Selection Popup */}
        <Popover
          open={Boolean(downloadSelectorAnchor)}
          anchorEl={downloadSelectorAnchor}
          onClose={handleDownloadClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              width: 250,
              maxHeight: 300,
              overflow: 'auto',
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
              Download Report
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                variant="text"
                onClick={() => handleDownloadConfirm('excel')}
                disabled={isDownloadLoading}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                Excel
              </Button>
              <Typography
                variant="body2"
                color="text.disabled"
                sx={{
                  textAlign: 'center',
                  fontStyle: 'italic',
                  mt: 1,
                }}
              >
                CSV download coming soon
              </Typography>
            </Box>
          </Box>
        </Popover>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid item xs={12}>
          {isLoading ? (
            <Box sx={{ width: '100%' }}>
              <Skeleton variant="rectangular" height={400} />
            </Box>
          ) : rowData.length > 0 ? (
            <Box sx={{ width: '100%', height: 400 }}>
              <DataGrid
                rows={getRowDataWithIds()}
                columns={getDataGridColumns()}
                pageSizeOptions={[10, 25, 50, 100]}
                initialState={{
                  pagination: {
                    paginationModel: { page: 0, pageSize: 25 },
                  },
                }}
                pagination
                disableRowSelectionOnClick
                sx={{
                  '& .MuiDataGrid-cell': {
                    borderRight: '1px solid',
                    borderColor: 'divider',
                  },
                  '& .MuiDataGrid-columnHeader': {
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                    fontWeight: 'bold',
                  },
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 200,
              }}
            >
              <Typography variant="h6" color="textSecondary">
                {noDataText()}
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default Report;
